import Service, { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import config from 'uplisting-frontend/config/environment';
import { FeatureEntitlement } from 'uplisting-frontend/models/customer';
import { DbePlan } from 'uplisting-frontend/models/duda-configuration';

export default class FeaturesService extends Service {
  @service session!: Services['session'];

  @service('feature-flags') featureFlags!: Services['feature-flags'];

  @service('repositories/account-configuration')
  accountConfigurationRepository!: Services['repositories/account-configuration'];

  @service('repositories/duda-configuration')
  dudaConfigurationRepository!: Services['repositories/duda-configuration'];

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  @cached
  get isDashboardEnabled(): boolean {
    return this.abovePlanVersion(3);
  }

  @cached
  get isClientManagementEnabledOnAccount(): boolean {
    return this.accountConfigurationRepository.currentAccountConfiguration
      .clientManagementEnabled;
  }

  @cached
  get isClientStatementsEnabledOnAccount(): boolean {
    return this.accountConfigurationRepository.currentAccountConfiguration
      .clientStatementsEnabled;
  }

  @cached
  get isDbePlusGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('dbe_plus');
  }

  @cached
  get isDbeTemplateChooserGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('dbe_template_chooser');
  }

  @cached
  get isAdaptGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('airdna_adapt_integration');
  }

  @cached
  get isLocalePickerEnabled(): boolean {
    return this.featureFlags.gate.enabled('locale_picker');
  }

  @cached
  get isProtectionGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('protection');
  }

  // The one place that decides who sees compliance: the sidebar link, the Action
  // Centre's compliance filter and the task panel's record link all read it. Not
  // cached, because a gate can change without a tracked update.
  get canSeeCompliance(): boolean {
    return (
      this.featureFlags.gate.enabled('compliance') &&
      (this.session.currentUser?.hasAccessTo('compliance.documents') ?? false)
    );
  }

  @cached
  get isTaskCentreEnabled(): boolean {
    return this.featureFlags.gate.enabled('task_centre');
  }

  @cached
  get isTeamPresetsEnabled(): boolean {
    return this.featureFlags.gate.enabled('team_presets');
  }

  @cached
  get isInboxAssignmentEnabled(): boolean {
    return this.featureFlags.gate.enabled('inbox_assignment');
  }

  @cached
  get isV5pricingEnabled(): boolean {
    return this.featureFlags.gate.enabled('v5_pricing');
  }

  @cached
  get isVrboConnectionManagementGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('vrbo_connection_management');
  }

  @cached
  get isDbePlusOnAccount(): boolean {
    return (
      this.dudaConfigurationRepository.currentDudaConfiguration?.dbePlan ===
      DbePlan.plus
    );
  }

  @cached
  get isAdvancedReportingEnabledOnAccount(): boolean {
    return this.abovePlanVersion(3);
  }

  @cached
  get isProratedClientAttributionEnabledGlobally(): boolean {
    return config.features.proratedClientAttribution;
  }

  @cached
  get isBdcPublishingEnabledGlobally(): boolean {
    return config.features.bdcPublishing;
  }

  @cached
  get isBdcPaymentsByBookingGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('bdc_payments_by_booking');
  }

  @cached
  get isMinimumStayAutomationGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('minimum_stay_automation');
  }

  @cached
  get isAirbnbListingRoomsGateEnabled(): boolean {
    return this.featureFlags.gate.enabled('airbnb_listing_rooms');
  }

  @cached
  get isAdaptBundled(): boolean {
    return this.isAddOnBundled('adapt');
  }

  @cached
  get isSmartLocksBundled(): boolean {
    return this.isAddOnBundled('smart_locks');
  }

  @cached
  get isProtectBundled(): boolean {
    return this.isAddOnBundled('security_deposits');
  }

  @cached
  get isClientManagementBundled(): boolean {
    return this.isAddOnBundled('client_management');
  }

  @cached
  get isClientStatementsBundled(): boolean {
    return this.isAddOnBundled('client_statements');
  }

  @cached
  get isDbePlusBundled(): boolean {
    return this.isAddOnBundled('dbe_plus');
  }

  @cached
  get isGuestIdentityBundled(): boolean {
    return this.isAddOnBundled('guest_identity_verification');
  }

  @cached
  get isGoogleVacationRentalsUpgradeRequired(): boolean {
    return (
      this.featureEntitlement('google_vacation_rentals') ===
      FeatureEntitlement.upgrade
    );
  }

  canSeeRooms(property: ModelRegistry['property']): boolean {
    return (
      this.isAirbnbListingRoomsGateEnabled && property.hasConfirmedAirbnbLink
    );
  }

  // The API is the gate — absent entitlements fail open to `included`.
  private featureEntitlement(feature: string): FeatureEntitlement {
    return (
      this.currentUser?.customer?.featureEntitlements?.[feature] ??
      FeatureEntitlement.included
    );
  }

  private isAddOnBundled(addOnCode: string): boolean {
    return (this.currentUser?.customer?.bundledAddons ?? []).includes(
      addOnCode,
    );
  }

  private abovePlanVersion(planVersion: number): boolean {
    if (this.currentUser.customer) {
      return this.currentUser.customer.planVersion >= planVersion;
    }

    return true;
  }
}

declare module '@ember/service' {
  interface Registry {
    features: FeaturesService;
  }
}
